import { BusinessAutomationActionTypeEnum } from '@reach/shared-types/business-automation';
import type { ActionData } from './actionTypeDefaults';

const {
  SEND_COMMUNICATION,
  WAIT_FOR_DURATION,
  WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME,
  WAIT_UNTIL_NEXT_OCCURRENCE,
  CHECK_SEGMENT_MEMBERSHIP,
} = BusinessAutomationActionTypeEnum;

// Type helpers for narrowing action types
export type SendCommunicationAction = Extract<ActionData, { type: typeof SEND_COMMUNICATION }>;

type WaitForDurationAction = Extract<ActionData, { type: typeof WAIT_FOR_DURATION }>;

type WaitUntilAction = Extract<
  ActionData,
  { type: typeof WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME } | { type: typeof WAIT_UNTIL_NEXT_OCCURRENCE }
>;

type CheckSegmentMembershipAction = Extract<ActionData, { type: typeof CHECK_SEGMENT_MEMBERSHIP }>;

// ENUM values are user facing!
export enum ActionSetupField {
  Subject = 'subject line',
  Html = 'email body',
  Text = 'message text',
  Duration = 'duration',
  Hour = 'hour',
  Minute = 'minute',
  Segments = 'segments',
}

export type SetupValidationResult = ActionSetupField[] | false;

type ActionSetupChecker = (action: ActionData) => SetupValidationResult;

function checkSendCommunicationNeedsSetup(action: ActionData): SetupValidationResult {
  const sendAction = action as SendCommunicationAction;
  const subject = sendAction.subject?.trim();
  const html = sendAction.html?.trim();
  const text = sendAction.text?.trim();

  const emailChannelComplete = subject && html;
  const smsChannelComplete = text;
  if (emailChannelComplete || smsChannelComplete) return false;

  const missingFields: ActionSetupField[] = [];
  if (!subject) missingFields.push(ActionSetupField.Subject);
  if (!html) missingFields.push(ActionSetupField.Html);
  if (!text) missingFields.push(ActionSetupField.Text);
  return missingFields;
}

function checkWaitForDurationNeedsSetup(action: ActionData): SetupValidationResult {
  const waitAction = action as WaitForDurationAction;
  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = waitAction;
  return days === 0 && hours === 0 && minutes === 0 && seconds === 0
    ? [ActionSetupField.Duration]
    : false;
}

function checkWaitUntilNeedsSetup(action: ActionData): SetupValidationResult {
  const waitUntilAction = action as WaitUntilAction;
  const missingFields: ActionSetupField[] = [];
  if (waitUntilAction.hour === undefined) missingFields.push(ActionSetupField.Hour);
  if (waitUntilAction.minute === undefined) missingFields.push(ActionSetupField.Minute);
  return missingFields.length > 0 ? missingFields : false;
}

function checkSegmentMembershipNeedsSetup(action: ActionData): SetupValidationResult {
  const segmentAction = action as CheckSegmentMembershipAction;
  return !segmentAction.segmentIds || segmentAction.segmentIds.length === 0
    ? [ActionSetupField.Segments]
    : false;
}

export function checkActionNeedsSetup(action: ActionData): SetupValidationResult {
  const checkers: Record<string, ActionSetupChecker> = {
    [SEND_COMMUNICATION]: checkSendCommunicationNeedsSetup,
    [WAIT_FOR_DURATION]: checkWaitForDurationNeedsSetup,
    [WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME]: checkWaitUntilNeedsSetup,
    [WAIT_UNTIL_NEXT_OCCURRENCE]: checkWaitUntilNeedsSetup,
    [CHECK_SEGMENT_MEMBERSHIP]: checkSegmentMembershipNeedsSetup,
  };
  const checker = checkers[action.type];
  return checker ? checker(action) : false;
}
